import { Link as RouterLink } from 'react-router-dom';
import { Stack, Table, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Link } from '@liminal/web-ui/primitives/Link';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { fontSizes } from '@liminal/brand/scale';
import { formatOrderDate } from '@liminal/utils/date';
import { NimCallInitiator } from '@/api/generated';
import type { NimCall, Profile } from '@/api/types';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { nimCallEndReasonLabel, nimCallInitiatorLabel, nimCallState } from '@/components/ui/statusHelpers';
import { callDurationText } from './nimCalls';

const COLUMNS = ['When', 'Placed by', 'User', 'About', 'State', 'Duration', 'End reason', 'Options', 'Transcript'] as const;

interface NimCallsTableProps {
  /** One page of calls, newest first, as the server ordered them. */
  calls: readonly NimCall[];
  /** The users on the page, keyed by id; a user missing from it shows as their id. */
  users: Readonly<Record<string, Profile>>;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  emptyMessage: string;
}

/** Calls between users and Nim: who placed each, what about, and how it went. */
export function NimCallsTable({ calls, users, isLoading, isError, errorMessage, emptyMessage }: NimCallsTableProps) {
  return (
    <Card variant="elevated" padding="none">
      <TableContainer>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <DataTableBody
            columns={COLUMNS.length}
            isLoading={isLoading}
            isError={isError}
            isEmpty={calls.length === 0}
            loadingRows={8}
            emptyMessage={emptyMessage}
            errorMessage={errorMessage}
          >
            {calls.map((call) => {
              const state = nimCallState(call.state);
              return (
                <TableRow key={call.callId} hover data-testid={`nim-call-${call.callId}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                    {formatOrderDate(call.createTime)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: fontSizes.xs }}>
                      {nimCallInitiatorLabel(call.initiator)}
                    </Typography>
                    {call.initiator === NimCallInitiator.NIM_CALL_INITIATOR_ADMIN && call.initiatorId && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: fontSizes.xs }}>
                        {call.initiatorId}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <UserIdLink userId={call.userId}>{users[call.userId]?.email || undefined}</UserIdLink>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    {/* The brief is on hover: it can run to 8000 characters. */}
                    <Typography variant="body2" noWrap title={call.brief || undefined} sx={{ fontSize: fontSizes.xs }}>
                      {call.reason || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusPill label={state.label} tone={state.tone} size="sm" />
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontSize: fontSizes.xs }}>
                    {callDurationText(call.liveDuration)}
                  </TableCell>
                  <TableCell sx={{ fontSize: fontSizes.xs }}>
                    {/* A call that rings or is live has no end reason yet. */}
                    {call.endTime ? nimCallEndReasonLabel(call.endReason) : '—'}
                  </TableCell>
                  <TableCell>
                    {call.waiveUserCredits || call.readOnlyTools ? (
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {call.waiveUserCredits && <StatusPill label="Credits waived" tone="neutral" size="sm" />}
                        {call.readOnlyTools && <StatusPill label="Read-only" tone="neutral" size="sm" />}
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: fontSizes.xs }}>
                    {call.conversationId ? (
                      <Link component={RouterLink} to={`/conversations/${call.conversationId}`}>
                        Transcript
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </DataTableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
